import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AudioMessagesPlayer from './AudioMessagesPlayer';
import ImageGallery from './ImageGallery';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './MessageComponents.css';
import { useCachedBlobUrl } from '../../Services/useBlobDownloader';
import { FileRender } from './FileRender';
import MessageArchiveDownload from './MessageArchiveDownload';
import { markdownRehypePlugins } from '../../utils/markdownSanitize';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

/**
 * ----------------------------
 * Incoming Messages (Assistant)
 * ----------------------------
 */

// 1. Assistant header message (intro message)
// (This one is left intact because its wrapper is separate.)
export const AssistantHeaderMessage = ({ message }) => {
  return (
    <motion.div className="assistant-header" exit={{ display: 'none' }}>
      {message.type === 'bot' ? (
        <>
          {message.replacebotlogo === 'yes' ? (
            <img className="avatar" src={message.logo} alt={message.newassistant} />
          ) : (
            <motion.div
              className="avatar bot"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, duration: 0.1 }}
            />
          )}
          <div className="assistant-info">
            <div className={message.showbotbadge === 'yes' ? 'bot-info' : 'name'}>
              {message.newassistant}
            </div>
            <div className="assistant-subtitle">{message.subscription}</div>
          </div>
        </>
      ) : (
        <>
          <img className="avatar" src={message.logo} alt={message.newassistant} />
          <div className="assistant-info">
            <div className="name">{message.newassistant}</div>
            <div className="assistant-subtitle">{message.subscription}</div>
          </div>
        </>
      )}
    </motion.div>
  );
};

/**
 * For all media and HTML components, we now return only the internal markup
 * that goes inside the message-bubble div.
 */

// 2. Media message from bot – URL
export const MediaMessageBotURL = ({ message }) => {
  return (
    <>
      <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.text) }}></p>
      <a className="link-wrapper" href={message.url} target="_blank" rel="noopener noreferrer">
        <div className="link-container">
          <img className="link-image" src={message.image} alt={message.url} />
          <div className="link-description">
            <span className="description-title">{message.title}</span>
            <div className="description-text">{message.description}</div>
          </div>
        </div>
      </a>
    </>
  );
};

// 3. Media message from bot – Image (single or gallery)
export const MediaMessageBotImage = ({ message }) => {
  return (
    <>
      <ImageGallery message={message} />
      <MessageArchiveDownload images={message.images} />
      {message.text && (
        <div className="gallery-message">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={markdownRehypePlugins}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer">
                  {props.children}
                </a>
              ),
            }}
          >
            {typeof message.text === 'string'
              ? message.text
              : JSON.stringify(message.text, null, 2)}
          </ReactMarkdown>
        </div>
      )}
    </>
  );
};

export const MediaMessageBotAudio = ({ message }) => {
  const audio = message?.audio?.[0];
  return (
    <>
      <AudioMessagesPlayer
        audioKey={audio?.key || null}
        histogramData={audio?.histogram || []}
        playbackTime={audio?.playback_time}
      />
      {message?.text && (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={markdownRehypePlugins}
          components={{
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer">
                {props.children}
              </a>
            ),
          }}
        >
          {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
        </ReactMarkdown>
      )}
    </>
  );
};

export const MediaMessageBotFile = ({ message }) => {
  return (
    <>
      {message.images && message.images.length > 0 && <ImageGallery message={message} />}

      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={markdownRehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <FileRender message={message} />
    </>
  );
};

// 6. Media message from bot – Video
export const MediaMessageBotVideo = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={markdownRehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <FileRender message={message} />
    </>
  );
};

export const MessageControls = ({ controls }) => {
  if (!Array.isArray(controls) || controls.length === 0) return null;

  const htmlControls = controls.filter(
    (control) => control?.content_type === 'text/html' && control?.content,
  );
  if (htmlControls.length === 0) return null;

  return (
    <>
      {htmlControls.map((control, index) => (
        <div key={`${control.name}-${index}`} className="message-row assistant no-badge">
          <div
            className="message-control html-content"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(control.content, { allowInlineStyles: true }),
            }}
          />
        </div>
      ))}
    </>
  );
};

// 7. Media message from bot – HTML
export const MediaMessageBotHTML = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={markdownRehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <div
        className="html-content"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.code) }}
      />
      <span className="timestamp">{message.timestamp}</span>
    </>
  );
};

/**
 * ----------------------------
 * Outbound Messages (User)
 * ----------------------------
 */

// 8. Media message from user – URL
export const MediaMessageUserURL = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={markdownRehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <a className="link-wrapper" href={message.url} target="_blank" rel="noopener noreferrer">
        <div className="link-container">
          <img className="link-image" src={message.image} alt={message.url} />
          <div className="link-description">
            {message.title && <span className="description-title">{message.title}</span>}
            {message.description && (
              <div className="description-text">
                <div className="text-container">{message.description}</div>
              </div>
            )}
          </div>
        </div>
      </a>
    </>
  );
};

// 9. Media message from user – Image (single or gallery)
export const MediaMessageUserImage = ({ message }) => {
  return (
    <>
      <ImageGallery message={message} />
      <MessageArchiveDownload images={message.images} />
      {message.text && (
        <div className="gallery-message">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={markdownRehypePlugins}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer">
                  {props.children}
                </a>
              ),
            }}
          >
            {typeof message.text === 'string'
              ? message.text
              : JSON.stringify(message.text, null, 2)}
          </ReactMarkdown>
        </div>
      )}
    </>
  );
};

// 10. Media message from user – Audio
export const MediaMessageUserAudio = ({ message }) => {
  const hasAudioArray = Array.isArray(message.audio) && message.audio.length > 0;
  const audioItem = hasAudioArray ? message.audio[0] : null;

  const hasText = !!message.text;

  if (!hasAudioArray && !hasText) {
    return null;
  }

  return (
    <>
      {hasAudioArray && audioItem && (
        <AudioMessagesPlayer
          audioKey={audioItem.key}
          histogramData={audioItem.histogram || []}
          playbackTime={audioItem.playback_time}
        />
      )}

      {hasText && (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={markdownRehypePlugins}
          components={{
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer">
                {props.children}
              </a>
            ),
          }}
        >
          {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
        </ReactMarkdown>
      )}
    </>
  );
};

// 11. Media message from user – File
export const MediaMessageUserFile = ({ message }) => {
  return (
    <>
      {message.images && message.images.length > 0 && <ImageGallery message={message} />}

      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={markdownRehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <FileRender message={message} />
    </>
  );
};

// 12. Media message from user – Video
export const MediaMessageUserVideo = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={markdownRehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <FileRender message={message} />
    </>
  );
};

// 13. Media message from user – HTML
export const MediaMessageUserHTML = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={markdownRehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <div
        className="html-content"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.code) }}
      />
      <span className="timestamp">{message.timestamp}</span>
    </>
  );
};
